// Imports
import { Plugin, Editor } from '@ckeditor/ckeditor5-core';
import { ViewDocumentFragment, ViewElement, ViewNode, ViewDocument, ViewText } from '@ckeditor/ckeditor5-engine';


// Plugin
export default class RestrictTableCellContent extends Plugin {
	// Handle plugin initialization
	init() {
		// Initialize
		const editor: Editor = this.editor;
		const restrictedInTableCell = [
			'blockQuote',
			'heading1',
			'heading2',
			'heading3',
			'listItem',
			'horizontalLine',
			'table',
		];
		
		
		// Restrict most block elements
		//  -> They will break Markdown table structure or won't format correctly
		editor.model.schema.extend('tableCell', {
			disallowChildren: restrictedInTableCell,
		});
		
		
		// Wait for toolbar to be present
		editor.ui.on('ready', () => {
			// Find the heading dropdown menu in the toolbar
			const toolbar = editor.ui.view.toolbar;
			const headingDropdown = toolbar?.items.find(
				(item) => item.element instanceof HTMLElement && item.element.classList.contains('ck-heading-dropdown')
			);
			
			
			// Disable the heading dropdown when inside a table
			editor.model.document.on('change', () => {
				const position = editor.model.document.selection.getFirstPosition();
				const insideTableCell = position?.findAncestor('tableCell');
				
				if (headingDropdown && 'isEnabled' in headingDropdown) {
					headingDropdown.isEnabled = !insideTableCell;
				}
			});
		});
		
		
		// Add key press event listener
		//  -> Prevent using "Enter" inside tableCell to stop new lines and paragraph splitting from breaking Markdown table structure
		editor.editing.view.document.on(
			'keydown',
			(event: unknown, data: unknown) => {
				// Type safety
				if (data && typeof data === 'object' && 'domEvent' in data && data.domEvent instanceof KeyboardEvent) {
					// Initialize
					const domEvent = data.domEvent;
					
					
					// Check for "Enter" key press
					if (domEvent.key === 'Enter') {
						// Get selection position
						const position = editor.model.document.selection.getFirstPosition();
						
						
						// Return if missing
						if (!position) {
							return;
						}
						
						
						// Check if we are in a table cell
						const tableCell = position.findAncestor('tableCell');
						
						if (tableCell) {
							// Block default action
							domEvent.preventDefault();
							
							
							// Type safety
							if (event && typeof event === 'object' && 'stop' in event && typeof event.stop === 'function') {
								// Stop processing event
								event.stop();
							}
						}
					}
				}
			},
			{ priority: 'high' }
		);
		
		
		// Initialize
		const viewDocument: ViewDocument = editor.editing.view.document;
		
		
		// Intercept pasted content before it's inserted into the editor
		editor.plugins.get('ClipboardPipeline').on(
			'inputTransformation',
			(
				_event,
				data: {
					content: ViewDocumentFragment | null;
				}
			) => {
				// Return early
				if (!data.content) {
					return;
				}
				
				
				// Get selection position
				const position = editor.model.document.selection.getFirstPosition();
				
				
				// Return if missing
				if (!position) {
					return;
				}
				
				
				// Check if we are in a table cell
				const tableCell = position.findAncestor('tableCell');
				
				
				// If not, return early
				if (!tableCell) {
					return;
				}
				
				
				// Initialize
				const newFragment = new ViewDocumentFragment(viewDocument);
				
				
				// Transform content
				const flattened = this._convertPastedContentInTableCell(data.content, viewDocument);
				
				
				// Add transformed content
				for (const node of flattened) {
					newFragment._appendChild(node);
				}
				
				
				// Set content to be inserted
				data.content = newFragment;
			}
		);
	}
	
	
	/** Helper function that will flatten pasted, allowed elements */
	private _convertPastedContentInTableCell = (
		fragment: ViewDocumentFragment | ViewElement,
		viewDocument: ViewDocument
	) => {
		// Initialize
		const allowedElements = new Set(['$text', 'span', 'strong', 'emphasis', 'link', 'imageInline']);
		const nodes: ViewNode[] = [];
		const children = Array.from(fragment.getChildren());
		let isFirstBlock = true;
		
		
		// Iterate over children
		for (const child of children) {
			// Check if node is an element
			if (child.is('element')) {
				if (allowedElements.has(child.name)) {
					// Keep allowed inline elements
					nodes.push(child);
				} else if (child.name === 'imageBlock' || child.name === 'img') {
					// Convert block image to inline image
					nodes.push(new ViewElement(viewDocument, 'img', new Map(child.getAttributes())));
				} else if (child.childCount > 0) {
					// Recursively flatten children of block elements
					const innerFlattened = this._convertPastedContentInTableCell(child, viewDocument);
					
					if (innerFlattened.length > 0) {
						// Add a space if not the first block
						if (!isFirstBlock && nodes.length > 0) {
							nodes.push(new ViewText(viewDocument, ' '));
						}
						
						
						// Add flattened nodes
						nodes.push(...innerFlattened);
						isFirstBlock = false;
					}
				}
			} else {
				// Add a space if not the first block
				if (!isFirstBlock && nodes.length > 0) {
					nodes.push(new ViewText(viewDocument, ' '));
				}
				
				
				// Keep text nodes or similar
				nodes.push(child);
				isFirstBlock = false;
			}
		}
		
		
		// Return updated nodes
		return nodes;
	};
	
	
	// The name this plugin can be referred to by
	public static get pluginName(): 'RestrictTableCellContent' {
		return 'RestrictTableCellContent';
	}
}
